import React, { useEffect, useState, useRef } from "react";
import {
  collection,
  onSnapshot,
  doc,
  query,
  where,
  getDocs,
  writeBatch,
  runTransaction,
  Timestamp,
  increment,
} from "firebase/firestore";
import { db } from "../config/firebaseConfig";
import Navigation from "../components/Navigation";
import SEO from "../components/SEO";
import PremiumAvantages from "../components/PremiumAvantages";
import HeroBgImg from "../assets/images/event.webp";

const formatDateFull = (timestamp) => {
  if (!timestamp) return "";
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return date.toLocaleDateString("fr-FR", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

// Durée pendant laquelle une place est réservée en attendant le paiement Stripe.
// Passé ce délai, la place est automatiquement relâchée pour les prochains visiteurs.
const HOLD_MINUTES = 20;

// Libère les places dont le paiement n'a jamais été finalisé (utilisateur parti
// sans payer). Appelé juste avant de vérifier les places disponibles.
const releaseExpiredHolds = async (sessionId, sessionRef) => {
  const staleQuery = query(
    collection(db, "inscriptions"),
    where("atelier_id", "==", sessionId),
    where("status", "==", "pending_payment"),
    where("held_until", "<=", Timestamp.now())
  );
  const staleSnap = await getDocs(staleQuery);
  if (staleSnap.empty) return;

  const batch = writeBatch(db);
  staleSnap.docs.forEach((d) => {
    batch.update(d.ref, { status: "expired" });
  });
  batch.update(sessionRef, { inscrits_count: increment(-staleSnap.size) });
  await batch.commit();
};

const formatDateParts = (timestamp) => {
  if (!timestamp) return null;
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return {
    day: date.getDate(),
    month: date.toLocaleDateString("fr-FR", { month: "long" }),
    year: date.getFullYear(),
  };
};

function AteliersPage() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSession, setSelectedSession] = useState(null);
  const [selectedTier, setSelectedTier] = useState("standard");
  const [form, setForm] = useState({ prenom: "", nom: "", email: "", tel: "" });
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const firstFieldRef = useRef(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("inscription") === "confirmed") {
      setConfirmed(true);
      window.history.replaceState({}, "", "/ateliers");
    }
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, "ateliers"),
      (snap) => {
        const data = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((s) => s.active !== false)
          .sort((a, b) => {
            const da = a.date?.toDate ? a.date.toDate() : new Date(a.date || 0);
            const db_ = b.date?.toDate ? b.date.toDate() : new Date(b.date || 0);
            return da - db_;
          });
        setSessions(data);
        setLoading(false);
      },
      () => setLoading(false)
    );
    return unsub;
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setSelectedSession(null);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    document.body.style.overflow = selectedSession ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [selectedSession]);

  useEffect(() => {
    if (selectedSession && firstFieldRef.current) {
      firstFieldRef.current.focus();
    }
  }, [selectedSession]);

  const openModal = (session, tier = "standard") => {
    setSelectedSession(session);
    setSelectedTier(tier);
    setForm({ prenom: "", nom: "", email: "", tel: "" });
    setFormError("");
    setSubmitting(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.prenom.trim() || !form.nom.trim() || !form.email.trim()) {
      setFormError("Veuillez remplir tous les champs obligatoires.");
      return;
    }
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRe.test(form.email)) {
      setFormError("Adresse email invalide.");
      return;
    }

    setSubmitting(true);
    setFormError("");

    try {
      const sessionRef = doc(db, "ateliers", selectedSession.id);
      let stripeLink = null;

      await releaseExpiredHolds(selectedSession.id, sessionRef);

      await runTransaction(db, async (transaction) => {
        const snap = await transaction.get(sessionRef);
        if (!snap.exists()) throw new Error("Session introuvable.");
        const data = snap.data();
        const count = data.inscrits_count || 0;
        const max = data.places_max || 6;
        if (count >= max) throw new Error("Cette session est complète.");

        const prix = selectedTier === "premium"
          ? (data.prix_premium || 50)
          : (data.prix_standard || 35);

        stripeLink = selectedTier === "premium"
          ? data.stripe_link_premium
          : data.stripe_link_standard;

        // Document public (sans données perso) : sert au comptage des places
        // et au nettoyage des réservations expirées, lisible sans exposer
        // les coordonnées des inscrits.
        const inscRef = doc(collection(db, "inscriptions"));
        transaction.set(inscRef, {
          atelier_id: selectedSession.id,
          atelier_title: selectedSession.title,
          atelier_date: selectedSession.date || null,
          tier: selectedTier,
          prix,
          created_at: Timestamp.now(),
          held_until: Timestamp.fromMillis(
            Date.now() + HOLD_MINUTES * 60 * 1000
          ),
          status: "pending_payment",
        });

        // Coordonnées de contact : dans une sous-collection séparée, jamais
        // lisible côté client (règles Firestore), consultable uniquement
        // depuis la console Firebase.
        transaction.set(doc(inscRef, "private", "contact"), {
          prenom: form.prenom.trim(),
          nom: form.nom.trim(),
          email: form.email.trim().toLowerCase(),
          tel: form.tel.trim(),
        });

        transaction.update(sessionRef, { inscrits_count: count + 1 });
      });

      if (stripeLink) {
        window.location.href = stripeLink;
      } else {
        setConfirmed(true);
        setSelectedSession(null);
      }
    } catch (err) {
      setFormError(err.message || "Une erreur est survenue. Veuillez réessayer.");
      setSubmitting(false);
    }
  };

  return (
    <main className="ateliers-page">
      <SEO
        title="Ateliers Photo"
        path="/ateliers"
        description="Neuf ateliers photo en petit groupe à Lyon — du mode auto au manuel, composition, rue, flash, événementiel, retouche Lightroom, argentique. 1h30 à 2h30 · 6 personnes max · Dès 35 €."
      />
      <Navigation />

      {/* ── Hero ── */}
      <section className="hero ateliers-hero">
        <div className="hero__bg" aria-hidden="true">
          <img src={HeroBgImg} alt="" loading="eager" />
        </div>
        <div className="hero__overlay" aria-hidden="true" />
        <div className="hero__content">
          <span className="ateliers-hero__label">Ateliers photo · Lyon</span>
          <h1 className="ateliers-hero__title">
            Apprenez à<br />shooter comme<br />un pro.
          </h1>
          <p className="ateliers-hero__sub">
            1h30 à 2h30 · 6 personnes max · Lyon & Villeurbanne
          </p>
        </div>
      </section>

      {/* ── Formats ── */}
      <section className="ateliers-intro">
        <div className="ateliers-intro__container">
          <div className="ateliers-intro__left">
            <h2 className="ateliers-intro__title">
              Neuf ateliers,<br />une palette complète.
            </h2>
            <p className="ateliers-intro__body">
              Six ateliers terrain de 1h30 (théorie · exercices sur place ·
              sortie · débrief) pour progresser pas à pas, complétés par trois ateliers
              spécialisés — événementiel, retouche Lightroom, argentique. Vous
              repartez avec une fiche mémo et un défi à réaliser seul. 6
              personnes max par session. Prévoyez votre appareil photo (reflex
              ou hybride), une carte SD et une batterie chargée.
            </p>
            <a href="#ateliers-sessions" className="ateliers-intro__cta">
              Voir les prochaines dates
            </a>
          </div>
          <div className="ateliers-intro__right">
            <div className="atelier-format">
              <span className="atelier-format__num">01</span>
              <div>
                <h3 className="atelier-format__name">Bien démarrer avec son appareil</h3>
                <span className="atelier-format__public">Débutants complets, avec ou sans appareil</span>
                <p className="atelier-format__desc">
                  Modes PASM, ouverture, vitesse, ISO, histogramme et mise au
                  point. Vous passez de l'Auto au semi-automatique et rentrez
                  avec 3 photos dont vous êtes fier — en Av, Tv et M. Pas
                  d'appareil ? Du matériel est prêté sur place, pour ceux qui
                  veulent d'abord tester si la photo leur plaît avant
                  d'investir.
                </p>
                <span className="atelier-format__meta">1h30 · Terrain · 35 €</span>
              </div>
            </div>
            <div className="atelier-format">
              <span className="atelier-format__num">02</span>
              <div>
                <h3 className="atelier-format__name">Maîtriser la lumière</h3>
                <span className="atelier-format__public">Problèmes d'exposition</span>
                <p className="atelier-format__desc">
                  Les 3 modes de mesure : matricielle, pondérée centrale, spot.
                  Lire la lumière naturelle selon sa direction, sa dureté et
                  son heure. Exposer correctement dans les forts contrastes.
                </p>
                <span className="atelier-format__meta">1h30 · Terrain · 35 €</span>
              </div>
            </div>
            <div className="atelier-format">
              <span className="atelier-format__num">03</span>
              <div>
                <h3 className="atelier-format__name">Le triangle d'exposition</h3>
                <span className="atelier-format__public">Ceux qui veulent tout contrôler</span>
                <p className="atelier-format__desc">
                  Le lien ouverture / vitesse / ISO et leurs effets collatéraux
                  (flou de bougé, bruit, profondeur de champ). Maîtriser le
                  mode Manuel pour exposer selon ses priorités artistiques.
                </p>
                <span className="atelier-format__meta">1h30 · Terrain · 35 €</span>
              </div>
            </div>
            <div className="atelier-format">
              <span className="atelier-format__num">04</span>
              <div>
                <h3 className="atelier-format__name">Composition &amp; cadrage</h3>
                <span className="atelier-format__public">Photos fades ou sans vie</span>
                <p className="atelier-format__desc">
                  Les 3 règles fondamentales : règle des tiers, lignes
                  directrices, respiration. Cadrage serré ou large selon
                  l'histoire à raconter. Les erreurs classiques à éviter.
                </p>
                <span className="atelier-format__meta">1h30 · Terrain · 35 €</span>
              </div>
            </div>
            <div className="atelier-format">
              <span className="atelier-format__num">05</span>
              <div>
                <h3 className="atelier-format__name">Photographie de rue</h3>
                <span className="atelier-format__public">Capturer la vie</span>
                <p className="atelier-format__desc">
                  Oser photographier des inconnus discrètement. Anticiper
                  l'instant décisif. Réglages rapides (Av, AF-C, rafale).
                  Les bases du droit à l'image en France.
                </p>
                <span className="atelier-format__meta">1h30 · Terrain · 35 €</span>
              </div>
            </div>
            <div className="atelier-format">
              <span className="atelier-format__num">06</span>
              <div>
                <h3 className="atelier-format__name">Flash &amp; contre-jour</h3>
                <span className="atelier-format__public">Extérieur lumineux</span>
                <p className="atelier-format__desc">
                  Silhouette volontaire (dramatique, graphique) ou sujet éclairé
                  avec flash fill-in (résultat naturel). Correction d'exposition
                  et mesure spot pour les forts contrastes.
                </p>
                <span className="atelier-format__meta">1h30 · Terrain · 35 €</span>
              </div>
            </div>
            <div className="atelier-format">
              <span className="atelier-format__num">07</span>
              <div>
                <h3 className="atelier-format__name">Couvrir un événement</h3>
                <span className="atelier-format__public">Clubs, associations, tournois sportifs</span>
                <p className="atelier-format__desc">
                  Photographier un tournoi de padel, un match ou une kermesse
                  associative : anticiper l'action, réglages rafale et AF-C,
                  gérer une lumière changeante entre intérieur et extérieur,
                  rester discret et efficace tout en couvrant l'ensemble de
                  l'événement.
                </p>
                <span className="atelier-format__meta">2h · Terrain · 45 €</span>
              </div>
            </div>
            <div className="atelier-format">
              <span className="atelier-format__num">08</span>
              <div>
                <h3 className="atelier-format__name">Retouche photo — Initiation Lightroom</h3>
                <span className="atelier-format__public">Débutants en post-traitement</span>
                <p className="atelier-format__desc">
                  Import et organisation du catalogue, corrections globales
                  (exposition, balance des blancs, contraste), retouches
                  locales simples et export optimisé. En distanciel (visio) ou
                  en présentiel, 6 participants max.
                </p>
                <span className="atelier-format__meta">2h · Distanciel ou présentiel · 45 €</span>
              </div>
            </div>
            <div className="atelier-format">
              <span className="atelier-format__num">09</span>
              <div>
                <h3 className="atelier-format__name">Argentique débutant</h3>
                <span className="atelier-format__public">Curieux de la pellicule</span>
                <p className="atelier-format__desc">
                  Chargement de la pellicule, réglages manuels sans écran de
                  contrôle, discipline du nombre de vues limité. Une pellicule
                  est fournie pendant l'atelier pour repartir avec vos propres
                  clichés à faire développer.
                </p>
                <span className="atelier-format__meta">2h-2h30 · Terrain · Pellicule fournie · 55 €</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <PremiumAvantages />

      {/* ── Sessions ── */}
      <section id="ateliers-sessions" className="ateliers-sessions">
        <div className="ateliers-sessions__container">
          <div className="ateliers-sessions__header">
            <h2 className="ateliers-sessions__title">Prochaines dates</h2>
            <p className="ateliers-sessions__sub">
              Le point de rendez-vous est communiqué par email après inscription.
            </p>
          </div>

          {loading && (
            <p className="ateliers-sessions__state">Chargement des sessions…</p>
          )}

          {!loading && sessions.length === 0 && (
            <div className="ateliers-sessions__state ateliers-sessions__state--empty">
              <p>Aucune session programmée pour le moment.</p>
              <p>
                <a href="/contact">Contactez-moi</a> pour être prévenu en priorité
                à l'ouverture des prochaines inscriptions.
              </p>
            </div>
          )}

          <div className="ateliers-cards">
            {sessions.map((session) => {
              const placesLeft =
                (session.places_max || 6) - (session.inscrits_count || 0);
              const isFull = placesLeft <= 0;
              const dateParts = session.date ? formatDateParts(session.date) : null;

              return (
                <article
                  key={session.id}
                  className={`atelier-card${isFull ? " atelier-card--full" : ""}`}
                >
                  {dateParts && (
                    <div className="atelier-card__date" aria-label={formatDateFull(session.date)}>
                      <span className="atelier-card__date-day">{dateParts.day}</span>
                      <span className="atelier-card__date-month">{dateParts.month}</span>
                      <span className="atelier-card__date-year">{dateParts.year}</span>
                    </div>
                  )}

                  <div className="atelier-card__body">
                    <h3 className="atelier-card__title">{session.title}</h3>
                    {session.description && (
                      <p className="atelier-card__desc">{session.description}</p>
                    )}
                    {session.description_premium && (
                      <p className="atelier-card__desc atelier-card__desc--premium">
                        <span className="atelier-card__desc-label">Premium —</span>{" "}
                        {session.description_premium}
                      </p>
                    )}
                    <div className="atelier-card__meta">
                      {session.heure && (
                        <span className="atelier-card__meta-item">{session.heure}</span>
                      )}
                      {session.lieu && (
                        <span className="atelier-card__meta-item">{session.lieu}</span>
                      )}
                    </div>
                  </div>

                  <div className="atelier-card__footer">
                    <span className={`atelier-card__spots ${isFull ? "atelier-card__spots--full" : "atelier-card__spots--ok"}`}>
                      {isFull
                        ? "Complet"
                        : `${placesLeft} place${placesLeft > 1 ? "s" : ""} restante${placesLeft > 1 ? "s" : ""}`}
                    </span>

                    {!isFull ? (
                      <div className="atelier-card__ctas">
                        <button
                          className="atelier-card__cta atelier-card__cta--standard"
                          onClick={() => openModal(session, "standard")}
                        >
                          {session.prix_standard ?? 35} € — Standard
                        </button>
                        <button
                          className="atelier-card__cta atelier-card__cta--premium"
                          onClick={() => openModal(session, "premium")}
                        >
                          {session.prix_premium ?? 50} € — Premium
                        </button>
                      </div>
                    ) : (
                      // Une place peut s'être libérée (paiement jamais finalisé) sans
                      // que le compteur affiché soit encore à jour — la transaction
                      // vérifie la vraie disponibilité au moment de l'envoi.
                      <button
                        className="atelier-card__cta atelier-card__cta--waitlist"
                        onClick={() => openModal(session, "standard")}
                      >
                        Tenter ma chance
                      </button>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Confirmation toast ── */}
      {confirmed && (
        <div className="ateliers-toast" role="status">
          <div className="ateliers-toast__inner">
            <div>
              <strong>Inscription enregistrée !</strong>
              <p>
                Votre place est réservée. Un email de confirmation Stripe vous
                sera envoyé après le paiement.
              </p>
              <p>
                Pensez à prévoir votre appareil photo (reflex ou hybride),
                une carte SD et une batterie chargée.
              </p>
            </div>
            <button
              className="ateliers-toast__close"
              onClick={() => setConfirmed(false)}
              aria-label="Fermer"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* ── Modal inscription ── */}
      {selectedSession && (
        <div
          className="ateliers-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-heading"
          onClick={(e) => {
            if (e.target === e.currentTarget) setSelectedSession(null);
          }}
        >
          <div className="ateliers-modal__panel">
            <button
              className="ateliers-modal__close"
              onClick={() => setSelectedSession(null)}
              aria-label="Fermer"
            >
              ×
            </button>

            <div className="ateliers-modal__session-info">
              <div className="ateliers-modal__session-top">
                <span className="ateliers-modal__session-name">
                  {selectedSession.title}
                </span>
                <span className={`ateliers-modal__tier-badge${selectedTier === "premium" ? " ateliers-modal__tier-badge--premium" : ""}`}>
                  {selectedTier === "premium" ? "Premium" : "Standard"}
                </span>
              </div>
              {selectedSession.date && (
                <span className="ateliers-modal__session-date">
                  {formatDateFull(selectedSession.date)}
                </span>
              )}
            </div>

            <form
              className="ateliers-modal__form"
              onSubmit={handleSubmit}
              noValidate
            >
              <h2 id="modal-heading" className="ateliers-modal__form-title">
                Vos informations
              </h2>

              <div className="ateliers-modal__row">
                <label className="ateliers-modal__field">
                  <span className="ateliers-modal__label">Prénom *</span>
                  <input
                    ref={firstFieldRef}
                    type="text"
                    value={form.prenom}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, prenom: e.target.value }))
                    }
                    autoComplete="given-name"
                    required
                    disabled={submitting}
                  />
                </label>
                <label className="ateliers-modal__field">
                  <span className="ateliers-modal__label">Nom *</span>
                  <input
                    type="text"
                    value={form.nom}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, nom: e.target.value }))
                    }
                    autoComplete="family-name"
                    required
                    disabled={submitting}
                  />
                </label>
              </div>

              <label className="ateliers-modal__field">
                <span className="ateliers-modal__label">Email *</span>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, email: e.target.value }))
                  }
                  autoComplete="email"
                  required
                  disabled={submitting}
                />
              </label>

              <label className="ateliers-modal__field">
                <span className="ateliers-modal__label">Téléphone</span>
                <input
                  type="tel"
                  value={form.tel}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, tel: e.target.value }))
                  }
                  autoComplete="tel"
                  disabled={submitting}
                />
              </label>

              {formError && (
                <p className="ateliers-modal__error" role="alert">
                  {formError}
                </p>
              )}

              <button
                type="submit"
                className="ateliers-modal__submit"
                disabled={submitting}
              >
                {submitting
                  ? "Traitement…"
                  : `Procéder au paiement — ${
                      selectedTier === "premium"
                        ? (selectedSession.prix_premium ?? 50)
                        : (selectedSession.prix_standard ?? 35)
                    } €`}
              </button>

              <p className="ateliers-modal__note">
                Paiement sécurisé via Stripe. Votre place est réservée
                pendant {HOLD_MINUTES} minutes le temps de finaliser le
                paiement.
              </p>
            </form>
          </div>
        </div>
      )}
    </main>
  );
}

export default AteliersPage;
